import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import AppSettings from "@appsettings";
import { useAppChromeVisible } from "@/components/contexts/appChromeContext";
import { getActiveDynamicPage } from "@services/dynamicPageService.js";
import { liveModelYearsQuery } from "@services/liveModelYearsQuery.js";
import {
  useFeatureFlags,
  isFeatureEligible,
} from "@services/featureFlagsService.js";

import { buildContentUrl } from "../../utils";
import {
  transformToMenuSections,
  transformUtilityButtons,
} from "../NavMenu/navMenuTransformers";

const NavDynamicPageContext = createContext(null);

// Stable empty array so useFeatureFlags' loading state (data === undefined)
// doesn't hand the derive-memo a fresh reference on every render.
const EMPTY_FLAGS = [];

/**
 * Extracts childData from a DP component node — same logic as
 * ComponentRenderer's ComponentNode, but without rendering.
 */
function extractChildData(node) {
  const childData = {};

  // Slot children (keyed by slot name)
  for (const child of node.children || []) {
    if (!child.slot) continue;
    if (child.options?.hidden) continue;
    childData[child.slot] = child;
  }

  // Groups (repeating slot data)
  if (node.groups) {
    for (const [groupKey, groupArray] of Object.entries(node.groups)) {
      childData[groupKey] = groupArray;
    }
  }

  return childData;
}

/**
 * Shell-level provider that fetches the nav Dynamic Page and transforms
 * it into the menuSections/utilityLinks/layoutConfig shape consumed by TopNav.
 *
 * Place this above TopNav in the component tree. TopNav reads the
 * assembled data from useNavDynamicPage() instead of fetching its own.
 */
export function NavDynamicPageProvider({ children }) {
  // State holds ONLY the raw nav Dynamic Page data + metadata. Transformed
  // (feature-gated) data is derived below via useMemo, never stored — so a
  // late-arriving nav page always transforms against the CURRENT feature-flag
  // state instead of a value captured by the fetch's async closure.
  const [rawNav, setRawNav] = useState(null);
  const [liveModelYears, setLiveModelYears] = useState(null);
  const [loading, setLoading] = useState(true);
  // Stays mounted while the chrome is hidden (it also wraps the routed
  // content), so it holds its requests back instead.
  const chromeVisible = useAppChromeVisible();

  const {
    data,
    isError: featureFlagsError,
    isLoading: featureFlagsLoading,
  } = useFeatureFlags({ enabled: chromeVisible });
  // Stable empty reference while flags load so the memo below doesn't churn.
  const featureFlags = data ?? EMPTY_FLAGS;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!chromeVisible) {
      return undefined;
    }

    let cancelled = false;

    const fetchNavPage = async () => {
      try {
        // Canonical page name is lowercase "mainnavigation" (the API lowercases
        // the name on read; matched explicitly here and in the seed migration).
        // Live years only filter the authored menu; without them it shows the
        // authored years (null map), so their failure must not lose the nav.
        const [page, activePairs] = await Promise.all([
          getActiveDynamicPage("navigation", "mainnavigation"),
          queryClient.fetchQuery(liveModelYearsQuery()).catch((error) => {
            console.error("Live model years unavailable for the nav:", error);
            return null;
          }),
        ]);
        if (cancelled) {
          return;
        }

        if (!page?.components?.length) {
          return;
        }

        // Find the navMenu component node in the page
        const navMenuNode = page.components.find((c) => c.type === "navMenu");
        if (!navMenuNode) {
          return;
        }

        const childData = extractChildData(navMenuNode);
        const options = navMenuNode.options || {};
        // Extract version from page record (e.g. "mainnavigation#3" → "3")
        const navigationVersion = (page.versionName || "").split("#")[1] || "1";

        setRawNav({ childData, options, navigationVersion });
        setLiveModelYears(activePairs);
      } catch (error) {
        console.error("Error fetching nav dynamic page:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchNavPage();
    return () => {
      cancelled = true;
    };
  }, [queryClient, chromeVisible]);

  // Derived transformed nav data. Recomputes automatically whenever the raw nav
  // data OR any feature-flag input changes — this is the fix for the stale
  // transform race: buttons/link gating always reflect the settled flag state.
  const derived = useMemo(() => {
    if (!rawNav) {
      return {
        menuSections: [],
        utilityLinks: null,
        layoutConfig: null,
        utilityButtons: [],
      };
    }
    return buildNavData(
      rawNav.childData,
      rawNav.options,
      featureFlags,
      featureFlagsError,
      featureFlagsLoading,
      liveModelYears,
    );
  }, [
    rawNav,
    featureFlags,
    featureFlagsError,
    featureFlagsLoading,
    liveModelYears,
  ]);

  const contextValue = useMemo(
    () => ({
      menuSections: derived.menuSections || [],
      utilityLinks: derived.utilityLinks || null,
      utilityButtons: derived.utilityButtons || [],
      navigationVersion: rawNav?.navigationVersion || null,
      loading,
    }),
    [derived, rawNav, loading],
  );

  return (
    <NavDynamicPageContext.Provider value={contextValue}>
      {children}
    </NavDynamicPageContext.Provider>
  );
}

/**
 * Hook for consuming nav DP data in TopNav or any shell component.
 */
export function useNavDynamicPage() {
  const context = useContext(NavDynamicPageContext);
  if (!context) {
    return {
      menuSections: [],
      utilityLinks: null,
      utilityButtons: [],
      layoutConfig: null,
      navigationVersion: null,
      loading: true,
    };
  }
  return context;
}

/**
 * Builds the final nav data shape from extracted childData and options.
 */
function buildNavData(
  childData,
  options,
  featureFlags,
  featureFlagsError,
  featureFlagsLoading,
  liveModelYears,
) {
  const sections = childData.sections || [];
  const linkItems = childData.linkItems || [];
  const vehiclesFeaturedCategories = childData.vehiclesFeaturedCategories || [];
  const utilityButtonEntries = childData.utilityButtons || [];

  const utilityLinks = {
    vinLookupUrl: childData.vinLookupUrl?.content || "/vinLookup",
    vinLookupLabel: childData.vinLookupLabel?.content || "VIN Lookup",
    profileUrl: childData.profileUrl?.content || "/myaccount",
    profileLabel: childData.profileLabel?.content || "Profile",
  };

  const layoutConfig = {
    menuStyle: options.variants?.[0] || "slideOut",
    panelWidth: options.panelWidth || "default",
    panelBackgroundColor: childData.panelBackgroundColor?.content || "",
  };

  const menuSections = transformToMenuSections({
    sections,
    linkItems,
    vehiclesFeaturedCategories,
    featureFlags,
    featureFlagsError,
    liveModelYearsBySeries: liveModelYears
      ? liveModelYears.reduce((latestBySeries, { model, year }) => {
          const previous = latestBySeries.get(model);
          if (!previous || year > previous) {
            latestBySeries.set(model, year);
          }
          return latestBySeries;
        }, new Map())
      : null,
  });

  const utilityButtons = transformUtilityButtons({
    utilityButtons: utilityButtonEntries,
    featureFlags,
    featureFlagsError,
    featureFlagsLoading,
  });

  return { menuSections, utilityLinks, layoutConfig, utilityButtons };
}

export default NavDynamicPageProvider;
